import { FC, ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAppSelector } from "../../hooks/redux";
import { routes } from "./routes";
import {
  isCompassusHhRpmClinic,
  isCompassusHhRpmSlimClinic,
} from "../../pages/CompassusNavigator/hhRpmClinics";

interface EligibilityRouteGuardProps {
  children: ReactNode;
}

/**
 * Gate the HH-RPM Eligibility worklist (`/workflows/eligibility`) behind the
 * same access rule the Compassus Navigator uses for HH-RPM behavior: the
 * `HH-RPM` beta feature AND an HH-RPM-enabled clinic (id allow-list or the
 * narrow "hh rpm" name fallback) — see
 * `pages/CompassusNavigator/hhRpmClinics`.
 *
 * Defense in depth: the worklist's data comes from the Navigator HH-journey
 * endpoints, which the backend gates independently. This guard makes the
 * client route unreachable (redirects to the Navigator) for users without
 * access so the page/chunk never renders for them.
 */
export const EligibilityRouteGuard: FC<EligibilityRouteGuardProps> = ({
  children,
}) => {
  const location = useLocation();
  const userClinic = useAppSelector((state) => state.clinic?.userClinic);
  const betaFeaturesAllowedList = useAppSelector(
    (state) => state.auth?.betaFeaturesAllowedList
  );

  const isEligibilityPath = location.pathname.startsWith(
    routes.workflowsEligibility.path
  );

  if (
    isEligibilityPath &&
    (!isCompassusHhRpmClinic(userClinic, betaFeaturesAllowedList ?? []) ||
      isCompassusHhRpmSlimClinic(userClinic, betaFeaturesAllowedList ?? []))
  ) {
    return <Navigate to={routes.evaluationAndManagement.path} replace />;
  }

  return <>{children}</>;
};
